import React from 'react';
import { CoachAvailabilityInterval, CoachAvailabilityInput, DayOfWeek } from 'globalTypes';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CoachingAvailabilityEditDayRow from './CoachingAvailabilityEditDayRow';
import CoachingConstants from 'coaching/CoachingConstants';
import Checkbox from 'components/Checkbox';
import Container from 'components/Container';
import Divider from 'components/Divider';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Label from 'components/Label';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import TimezoneHelper from 'lib/timezones';
import EventTracking from 'lib/EventTracking';

import { getDayOfWeekIndex } from 'coaching/lib/timeIntervals';

type Props = {
  availabilitiesByDay: CoachAvailabilityInput[];
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  onClickSetAvailability: () => void;
  onAvailabilityChange: (
    day: DayOfWeek,
    intervals: CoachAvailabilityInterval[],
    maxSessions?: number
  ) => void;
  editMaxSessions: boolean;
  toggleEditMaxSessions: () => void;
  isSubmitEnabled: boolean;
};

export default class CoachingAvailabilityEditView extends React.Component<Props> {
  findAvailabilityByDay = (dayOfWeek: DayOfWeek) => {
    return this.props.availabilitiesByDay[getDayOfWeekIndex(dayOfWeek)];
  };
  findIntervalsByDay = (dayOfWeek: DayOfWeek) => {
    return this.findAvailabilityByDay(dayOfWeek)?.timeSlot.intervals;
  };
  findMaxSessionsByDay = (dayOfWeek: DayOfWeek) => {
    return this.findAvailabilityByDay(dayOfWeek)?.maxSessions;
  };

  componentDidMount() {
    EventTracking.trackKey('coaching.set_coach_availability.view');
  }

  render() {
    const {
      timezone,
      onTimezoneChange,
      onAvailabilityChange,
      onClickSetAvailability,
      editMaxSessions,
      toggleEditMaxSessions,
      isSubmitEnabled,
    } = this.props;

    return (
      <Container size="xsmall">
        <Heading serif>Set your normal availability</Heading>
        <Text element="p" lineHeight="large">
          Add the hours you’re available to book sessions for a typical week. For one-offs like
          appointments or vacations, add time off later.
        </Text>
        <FormElement
          label="Time zone"
          onValidChange={data => {
            onTimezoneChange(data ?? ''); // Prevents clearing the component from setting the value to undefined
            EventTracking.trackKey('coaching.set_coach_availability.select_time_zone.click');
          }}
          element={Select}
          elementProps={{
            options: TimezoneHelper.USTimezoneSelectOptions.filter(
              option => !CoachingConstants.TIMEZONES_TO_EXCLUDE.includes(option.label)
            ),
            value: timezone,
            searchable: false,
          }}
        />
        <Label>Availability</Label>
        <Text color="graphite">
          Peak Remind Tutoring hours are 5:00 – 9:00 PM across time zones.
        </Text>
        <Divider size="medium" />
        {CoachingConstants.VALID_COACHING_DAYS.map(day => (
          <CoachingAvailabilityEditDayRow
            key={day}
            dayOfWeek={day}
            initialIntervals={this.findIntervalsByDay(day)}
            maxSessions={this.findMaxSessionsByDay(day) ?? undefined}
            editingMaxSessions={editMaxSessions}
            onAvailabilityChange={onAvailabilityChange}
          />
        ))}
        <Row>
          <Heading level="5">Max daily sessions (optional)</Heading>
          <Checkbox
            label="Set a maximum number of desired sessions per day"
            checked={editMaxSessions}
            onChange={() => {
              toggleEditMaxSessions();
              EventTracking.trackKey(
                'coaching.set_coach_availability.toggle_editing_max_sessions.click'
              );
            }}
          />
        </Row>
        <Buttons full>
          <Button
            data-test="set-availability-button"
            eventKey="coaching.set_coach_availability.set_availability.click"
            onClick={onClickSetAvailability}
            disabled={!isSubmitEnabled}>
            Set availability
          </Button>
        </Buttons>
      </Container>
    );
  }
}
